import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Typography, Table, message, Select } from 'antd';
import { injectIntl, useIntl } from 'react-intl';
import styled from 'styled-components';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { AnyAction, compose } from '@reduxjs/toolkit';

import { T } from '@app/components';
import { requestGetNotifications, requestSendNotification, resetState } from './reducer';
import { selectNotifications, selectSendNotificationsLoading, selectGetNotificationsLoading } from './selectors';
import { createStructuredSelector } from 'reselect';
import { translationMessages } from '@app/i18n';

const { Title } = Typography;

const NotificationCenterContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 2rem;
`;

const DataTable = styled(Table)`
  margin-top: 3rem;
`;

type NotificationContainerProps = {
  notifications: any;
  sendNotificationsLoading: boolean;
  getNotificationsLoading: boolean;
  dispatchGetNotifications: () => AnyAction;
  dispatchSendNotification: (notification: any) => AnyAction;
  dispatchResetState: () => AnyAction;
};

const NotificationContainer = (props: NotificationContainerProps) => {
  const [form] = Form.useForm();

  const intl = useIntl();

  useEffect(() => {
    props.dispatchGetNotifications();
  }, []);

  const getColumns = () => {
    const fixedColumns: any = [
      {
        title: 'Sent At',
        dataIndex: 'sentAt',
        width: 120,
        fixed: 'left',
        render: (text: string) => moment.utc(text).tz('America/Chicago').format('DD/MM/YYYY HH:mm')
      },
      {
        title: 'Environment',
        dataIndex: 'environment',
        width: 100,
        render: (env: string) => (
          <span
            style={{
              color: env === 'prod' ? '#ff4d4f' : '#52c41a',
              fontWeight: 'bold',
              textTransform: 'uppercase'
            }}
          >
            {env}
          </span>
        )
      },
      {
        title: <span>{translationMessages.en.notification_title}</span>,
        dataIndex: 'notificationTitle'
      },
      {
        title: <span>{translationMessages.en.notification_content}</span>,
        dataIndex: 'notificationBody'
      },
      {
        title: 'Total Endpoints',
        dataIndex: 'totalEndpoints',
        width: 120
      },
      {
        title: 'Successful Sends',
        dataIndex: 'successfulSends',
        width: 120
      }
    ];

    return fixedColumns;
  };

  const handleSubmit = (values: any) => {
    const payload = {
      notificationTitle: values.title,
      notificationBody: values.body,
      environment: values.environment
    };

    props.dispatchSendNotification(payload);

    form.resetFields();
  };

  return (
    <NotificationCenterContainer>
      <Title level={3}>
        <T id="notification_center" />
      </Title>

      <Form layout="vertical" form={form} onFinish={handleSubmit} style={{ maxWidth: 600 }}>
        <Form.Item
          label="Environment"
          name="environment"
          rules={[{ required: true, message: 'Please select environment' }]}
        >
          <Select placeholder="Select environment to send notification">
            <Select.Option value="dev">
              <span style={{ color: '#52c41a', fontWeight: 'bold' }}>DEV</span>
              <span style={{ marginLeft: 8, color: '#666' }}>- Development users</span>
            </Select.Option>
            <Select.Option value="prod">
              <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>PROD</span>
              <span style={{ marginLeft: 8, color: '#666' }}>- Production users</span>
            </Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ id: 'notification_title' })}
          name="title"
          rules={[{ required: true, message: 'Please enter notification title' }]}
        >
          <Input placeholder={intl.formatMessage({ id: 'enter_notification_title' })} />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ id: 'notification_content' })}
          name="body"
          rules={[{ required: true, message: 'Please enter notification content' }]}
        >
          <Input.TextArea placeholder={intl.formatMessage({ id: 'enter_notification_body' })} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={props.sendNotificationsLoading}>
            Send Notification Now
          </Button>
        </Form.Item>
      </Form>
      <DataTable
        bordered={true}
        virtual
        columns={getColumns().map((col: any) => ({ ...col, onCell: undefined }))}
        scroll={{ x: 2000, y: 300 }}
        rowKey="id"
        dataSource={props.notifications}
        pagination={false}
        loading={props.getNotificationsLoading}
      />
    </NotificationCenterContainer>
  );
};

NotificationContainer.propTypes = {
  notifications: PropTypes.array,
  sendNotificationsLoading: PropTypes.bool,
  getNotificationsLoading: PropTypes.bool,
  dispatchGetNotifications: PropTypes.func,
  dispatchSendNotification: PropTypes.func,
  dispatchResetState: PropTypes.func
};

const mapStateToProps = createStructuredSelector({
  notifications: selectNotifications(),
  sendNotificationsLoading: selectSendNotificationsLoading(),
  getNotificationsLoading: selectGetNotificationsLoading()
});

export function mapDispatchToProps(dispatch: (arg0: AnyAction) => any) {
  return {
    dispatchGetNotifications: () => dispatch(requestGetNotifications({})),
    dispatchSendNotification: (notificationBody: any) => dispatch(requestSendNotification({ notificationBody })),
    dispatchResetState: () => dispatch(resetState())
  };
}
const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect, injectIntl)(NotificationContainer);
